import { ApiType } from "@gymflow/common";
import { ApiListResponse, FacilityDTO } from "@gymflow/types";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";

import { facilityQueryKeys } from "./facilityQueryKeys";
import { useFacilityCategories } from "./useFacilityCategories";

export async function facilityListQueryFn({
  api,
  opts,
}: {
  api: ApiType;
  opts?: Parameters<ApiType["facilityApi"]["find"]>[0];
}) {
  return (await api.facilityApi.find({ ...opts })).data;
}

export function useFacilityList(
  {
    api,
    opts,
  }: {
    api: ApiType;
    opts?: Parameters<ApiType["facilityApi"]["find"]>[0];
  },
  queryOpts?: Omit<
    UseQueryOptions<
      ApiListResponse<FacilityDTO>,
      unknown,
      ApiListResponse<FacilityWithCategoryNameDTO>
    >,
    "queryKey"
  >,
) {
  const categories = useFacilityCategories({ api });

  return useQuery({
    queryKey: facilityQueryKeys.list(opts),
    queryFn: () => facilityListQueryFn({ api, opts }),
    enabled: categories.isSuccess,
    select: (facilities: ApiListResponse<FacilityDTO>) => {
      return {
        ...facilities,
        content: facilities.content.map((facility) => {
          const facilityCategory = categories.data!.find(
            (category) => category.id === facility.facilityCategoryId,
          )?.name;
          const facilityWithCategoryName: FacilityWithCategoryNameDTO = {
            ...facility,
            categoryName: facilityCategory || "",
          };
          return facilityWithCategoryName;
        }),
      } as ApiListResponse<FacilityWithCategoryNameDTO>;
    },
    ...queryOpts,
  });
}

export interface FacilityWithCategoryNameDTO extends FacilityDTO {
  categoryName: string;
}
